import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { createLinkCode } from '../lib/api';

type OS = 'windows' | 'macos' | 'linux';

export default function Download() {
  const { user } = useAuth();
  const [detectedOS, setDetectedOS] = useState<OS>('windows');
  const [linkCode, setLinkCode] = useState<string>('');
  const [linkCodeLoading, setLinkCodeLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);

  useEffect(() => {
    // 모바일 감지
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'mobile', 'tablet'];
      return mobileKeywords.some(keyword => userAgent.includes(keyword));
    };
    setIsMobile(checkMobile());

    // OS 자동 감지
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) {
      setDetectedOS('windows');
    } else if (userAgent.includes('mac')) {
      setDetectedOS('macos');
    } else if (userAgent.includes('linux')) {
      setDetectedOS('linux');
    }
  }, []);

  // 인증된 사용자인 경우 앱 연동 코드 생성
  useEffect(() => {
    const generateCode = async () => {
      if (!user) {
        setLinkCode('');
        return;
      }
      
      setLinkCodeLoading(true);
      try {
        const response = await createLinkCode();
        setLinkCode(response.code);
      } catch (err) {
        console.error('Failed to generate link code:', err);
        setLinkCode('');
      } finally {
        setLinkCodeLoading(false);
      }
    };
    
    generateCode();
  }, [user]);

  const copyLinkCode = () => {
    navigator.clipboard.writeText(linkCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (_file: string) => {
    setDownloadStarted(true);
    // 실제 다운로드는 a 태그의 기본 동작으로 처리됨
  };

  const APP_VERSION = '0.8.17';
  
  const downloads: Record<OS, { name: string; icon: string; file: string; size: string; altFormats?: { name: string; file: string }[] }> = {
    windows: {
      name: 'Windows',
      icon: '🪟',
      file: `moldClaw_${APP_VERSION}_x64-setup.exe`,
      size: '5 MB',
    },
    macos: {
      name: 'macOS',
      icon: '🍎',
      file: `moldClaw_${APP_VERSION}_universal.dmg`,
      size: '8 MB',
    },
    linux: {
      name: 'Linux',
      icon: '🐧',
      file: `moldClaw_${APP_VERSION}_amd64.deb`,
      size: '4 MB',
      altFormats: [
        { name: 'RPM', file: `moldClaw_${APP_VERSION}_x86_64.rpm` },
        { name: 'AppImage', file: `moldClaw_${APP_VERSION}_x86_64.AppImage` },
      ],
    },
  };

  const otherOSes = (Object.keys(downloads) as OS[]).filter(os => os !== detectedOS);

  return (
    <div className="py-12">
      <div className="section-container">
        {/* Mobile Warning Banner */}
        {isMobile && (
          <div className="mb-8 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📱</span>
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-1">
                  모바일에서 접속하셨네요!
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                  moldClaw는 <strong>데스크톱 전용 앱</strong>입니다. PC나 Mac에서 이 페이지에 접속해 주세요.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('링크가 복사되었습니다! PC에서 붙여넣기 하세요.');
                    }}
                    className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    📋 링크 복사하기
                  </button>
                  <a
                    href={`mailto:?subject=moldClaw 다운로드 링크&body=PC에서 이 링크를 열어주세요: ${window.location.href}`}
                    className="px-3 py-1.5 bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-100 text-sm rounded-lg hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors"
                  >
                    ✉️ 이메일로 보내기
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white dark:text-pure-white mb-4">
            moldClaw 다운로드
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300 dark:text-zinc-400">
            당신의 AI를 데스크톱과 연결하세요
          </p>
        </div>

        {/* Primary Download - Detected OS */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="card text-center border-2 border-space-blue bg-gradient-to-b from-blue-50 to-white dark:from-zinc-800 dark:to-zinc-900 p-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-space-blue/10 text-space-blue text-sm font-medium rounded-full mb-4">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {downloads[detectedOS].name}용 자동 감지됨
            </div>
            
            <div className="text-5xl mb-4">{downloads[detectedOS].icon}</div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              moldClaw for {downloads[detectedOS].name}
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              버전 {APP_VERSION} • {downloads[detectedOS].size}
            </p>
            
            <a
              href={`/releases/${downloads[detectedOS].file}`}
              onClick={() => handleDownload(downloads[detectedOS].file)}
              className="inline-flex items-center justify-center gap-3 w-full max-w-sm mx-auto px-8 py-4 bg-space-blue hover:bg-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02]"
              download
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              무료 다운로드
            </a>
            
            {/* Linux 대체 포맷 */}
            {downloads[detectedOS].altFormats && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-zinc-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">다른 패키지 형식:</p>
                <div className="flex justify-center gap-3">
                  {downloads[detectedOS].altFormats!.map((alt) => (
                    <a
                      key={alt.name}
                      href={`/releases/${alt.file}`}
                      className="text-sm px-4 py-2 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors"
                      download
                    >
                      {alt.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Other OS Options */}
        <div className="max-w-xl mx-auto mb-12">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            다른 운영체제용 다운로드
          </p>
          <div className="flex justify-center gap-4">
            {otherOSes.map((os) => (
              <a
                key={os}
                href={`/releases/${downloads[os].file}`}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors"
                download
              >
                <span>{downloads[os].icon}</span>
                <span className="text-sm font-medium">{downloads[os].name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-500">({downloads[os].size})</span>
              </a>
            ))}
          </div>
        </div>

        {/* Download Started - Next Steps */}
        {downloadStarted && (
          <div className="max-w-2xl mx-auto mb-12 animate-fade-in">
            <div className="card bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-700">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
                    다운로드가 시작되었습니다! 🎉
                  </h3>
                  <div className="text-sm text-green-700 dark:text-green-300 space-y-2">
                    <p className="font-medium">다음 단계:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-1">
                      <li>다운로드된 파일을 실행하여 설치하세요</li>
                      <li>앱이 실행되면 아래의 <strong>연동 코드</strong>를 입력하세요</li>
                      <li>끝! 바로 사용할 수 있습니다</li>
                    </ol>
                  </div>
                  <p className="mt-3 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    설치 후 이 페이지로 돌아올 필요 없습니다. 앱에서 모든 것을 진행하세요!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* App Link Code */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="card bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-zinc-800 dark:to-zinc-800 border-space-blue/30">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-space-blue/10 rounded-full flex items-center justify-center">
                <span className="text-2xl">🔗</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">앱 연동 코드</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  moldClaw 앱 설치 후 이 코드를 입력하면 자동으로 계정이 연결됩니다.
                </p>
                
                {!user ? (
                  // 미인증 상태
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                      연동 코드를 받으려면 로그인이 필요합니다.
                    </p>
                    <Link 
                      to="/login" 
                      className="inline-flex items-center gap-2 px-4 py-2 bg-space-blue text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      로그인하기
                    </Link>
                  </div>
                ) : linkCodeLoading ? (
                  // 로딩 상태
                  <div className="flex gap-2">
                    <div className="flex-1 px-4 py-3 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg text-center">
                      <div className="animate-pulse h-6 bg-gray-200 dark:bg-zinc-700 rounded"></div>
                    </div>
                    <button
                      disabled
                      className="px-5 py-3 bg-gray-300 dark:bg-zinc-700 text-gray-500 rounded-lg font-medium cursor-not-allowed"
                    >
                      복사
                    </button>
                  </div>
                ) : linkCode ? (
                  // 코드 표시
                  <>
                    <div className="flex gap-2">
                      <code className="flex-1 px-4 py-3 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg font-mono text-lg text-gray-900 dark:text-white text-center tracking-wider">
                        {linkCode}
                      </code>
                      <button
                        onClick={copyLinkCode}
                        className="px-5 py-3 bg-space-blue text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        {copied ? '✓ 복사됨' : '복사'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      24시간 후 만료 • 새 코드가 필요하면 페이지 새로고침
                    </p>
                  </>
                ) : (
                  // 코드 생성 실패
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      연동 코드를 생성하는데 실패했습니다. 페이지를 새로고침 해주세요.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Installation Guide */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            설치 가이드
          </h2>
          
          <div className="space-y-4">
            {/* Windows */}
            <details className="card group" open={detectedOS === 'windows'}>
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🪟 Windows 설치 방법
                </h3>
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 text-gray-700 dark:text-gray-300">
                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-space-blue text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <p className="font-medium">설치 파일 실행</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">다운로드한 <code className="bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 rounded text-xs">moldClaw_{APP_VERSION}_x64-setup.exe</code> 파일을 더블클릭합니다.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-space-blue text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <p className="font-medium">보안 경고 처리</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">"Windows의 PC 보호" 경고 시 → <strong>추가 정보</strong> 클릭 → <strong>실행</strong> 클릭</p>
                      <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-xs text-amber-700 dark:text-amber-300">
                        💡 이 경고는 아직 Microsoft 인증을 받지 않은 새 앱에서 나타나는 정상적인 메시지입니다.
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-space-blue text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <p className="font-medium">설치 완료</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">설치 마법사 안내에 따라 진행하면 바탕화면에 moldClaw 아이콘이 생성됩니다.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">✓</div>
                    <div>
                      <p className="font-medium">앱 실행 후 연동 코드 입력</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">위에서 복사한 연동 코드를 입력하면 준비 완료!</p>
                    </div>
                  </div>
                </div>
              </div>
            </details>

            {/* macOS */}
            <details className="card group" open={detectedOS === 'macos'}>
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🍎 macOS 설치 방법
                </h3>
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 text-gray-700 dark:text-gray-300">
                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-space-blue text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <p className="font-medium">DMG 파일 열기</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">다운로드한 <code className="bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 rounded text-xs">moldClaw_{APP_VERSION}_universal.dmg</code> 파일을 더블클릭합니다.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-space-blue text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <p className="font-medium">앱 설치</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">moldClaw 아이콘을 Applications 폴더로 드래그합니다.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-space-blue text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <p className="font-medium">첫 실행 시 보안 설정</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">"확인되지 않은 개발자" 경고가 나타나면:</p>
                      <ol className="mt-2 text-sm text-gray-500 dark:text-gray-400 list-disc list-inside space-y-1">
                        <li><strong>시스템 설정</strong> → <strong>개인정보 보호 및 보안</strong> 이동</li>
                        <li>하단의 "moldClaw" 옆 <strong>확인 없이 열기</strong> 클릭</li>
                      </ol>
                      <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-xs text-amber-700 dark:text-amber-300">
                        💡 Apple 공증을 받기 전까지 나타나는 정상적인 보안 절차입니다.
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">✓</div>
                    <div>
                      <p className="font-medium">Launchpad에서 실행</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">연동 코드를 입력하면 바로 사용할 수 있습니다!</p>
                    </div>
                  </div>
                </div>
              </div>
            </details>

            {/* Linux */}
            <details className="card group" open={detectedOS === 'linux'}>
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🐧 Linux 설치 방법
                </h3>
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 text-gray-700 dark:text-gray-300">
                <div className="space-y-6">
                  {/* Ubuntu/Debian */}
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <span className="text-sm">📦</span> Ubuntu / Debian (.deb)
                    </h4>
                    <code className="block bg-gray-900 dark:bg-zinc-950 text-green-400 p-4 rounded-lg text-sm font-mono">
                      <span className="text-gray-500">$</span> sudo dpkg -i moldClaw_{APP_VERSION}_amd64.deb
                    </code>
                  </div>
                  
                  {/* Fedora/RHEL */}
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <span className="text-sm">📦</span> Fedora / RHEL (.rpm)
                    </h4>
                    <code className="block bg-gray-900 dark:bg-zinc-950 text-green-400 p-4 rounded-lg text-sm font-mono">
                      <span className="text-gray-500">$</span> sudo rpm -i moldClaw_{APP_VERSION}_x86_64.rpm
                    </code>
                  </div>
                  
                  {/* AppImage */}
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <span className="text-sm">📦</span> AppImage (모든 배포판)
                    </h4>
                    <code className="block bg-gray-900 dark:bg-zinc-950 text-green-400 p-4 rounded-lg text-sm font-mono whitespace-pre">
<span className="text-gray-500">$</span> chmod +x moldClaw_{APP_VERSION}_x86_64.AppImage{'\n'}<span className="text-gray-500">$</span> ./moldClaw_{APP_VERSION}_x86_64.AppImage</code>
                  </div>
                  
                  <div className="flex gap-4 items-start pt-2">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">✓</div>
                    <div>
                      <p className="font-medium">설치 완료</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">앱 실행 후 연동 코드를 입력하면 바로 사용할 수 있습니다!</p>
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </div>
        </div>

        {/* System Requirements */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            시스템 요구사항
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                🪟 Windows
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Windows 10 이상 (64-bit)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>RAM: 최소 4GB, 권장 8GB</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>저장공간: 500MB 이상</span>
                </li>
              </ul>
            </div>
            <div className="card">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                🍎 macOS
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>macOS 11 (Big Sur) 이상</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Intel 또는 Apple Silicon</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>RAM: 최소 4GB, 권장 8GB</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>저장공간: 500MB 이상</span>
                </li>
              </ul>
            </div>
            <div className="card">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                🐧 Linux
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Ubuntu 20.04+, Debian 11+</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Fedora 35+, Arch Linux</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>RAM: 최소 4GB, 권장 8GB</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>저장공간: 500MB 이상</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            자주 묻는 질문
          </h2>
          <div className="space-y-4">
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-gray-900 dark:text-white">
                <span className="flex items-center gap-2">
                  <span>🔒</span> 설치할 때 보안 경고가 나타나요
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                moldClaw는 아직 대형 벤더 인증(Microsoft/Apple)을 받지 않은 새로운 앱입니다. 위의 OS별 설치 가이드에서 보안 설정을 우회하는 방법을 확인하세요. 앱은 완전히 안전하며, 오픈소스로 코드를 직접 확인할 수도 있습니다.
              </p>
            </details>
            
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-gray-900 dark:text-white">
                <span className="flex items-center gap-2">
                  <span>🔗</span> 앱 연동 코드가 작동하지 않아요
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                연동 코드는 24시간 후 만료됩니다. 이 페이지를 새로고침하면 새로운 코드를 받을 수 있습니다. 또한 코드를 정확히 입력했는지 (대시 포함) 확인해 주세요.
              </p>
            </details>
            
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-gray-900 dark:text-white">
                <span className="flex items-center gap-2">
                  <span>📥</span> 다운로드가 시작되지 않아요
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                브라우저의 팝업 차단 기능이 다운로드를 막을 수 있습니다. 다운로드 버튼을 우클릭하고 "다른 이름으로 링크 저장"을 시도해 보세요. 또는 브라우저 설정에서 이 사이트의 다운로드를 허용해 주세요.
              </p>
            </details>
            
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-gray-900 dark:text-white">
                <span className="flex items-center gap-2">
                  <span>🔑</span> 어떤 권한이 필요한가요?
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                <p className="mb-2">moldClaw는 AI 기능을 제공하기 위해 다음 권한이 필요합니다:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-500 dark:text-gray-400">
                  <li><strong>파일 시스템 접근</strong>: 문서 작업 지원</li>
                  <li><strong>화면 캡처</strong>: 화면 기반 도움 기능 (선택적)</li>
                  <li><strong>네트워크</strong>: AI 서버와 통신</li>
                </ul>
                <p className="mt-2">모든 권한은 앱 내에서 개별적으로 관리할 수 있습니다.</p>
              </div>
            </details>
            
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-gray-900 dark:text-white">
                <span className="flex items-center gap-2">
                  <span>🔄</span> 기존 버전에서 업데이트하려면?
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                moldClaw는 자동 업데이트를 지원합니다. 앱이 실행 중일 때 새 버전이 있으면 자동으로 알림을 받습니다. 수동으로 업데이트하려면 새 버전을 다운로드하여 기존 앱 위에 설치하면 됩니다. 설정과 데이터는 유지됩니다.
              </p>
            </details>
          </div>
        </div>

        {/* Help Section */}
        <div className="max-w-4xl mx-auto">
          <div className="card bg-gray-50 dark:bg-zinc-800/50 border-gray-200 dark:border-zinc-700 text-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              추가 도움이 필요하신가요?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              설치나 사용 중 문제가 있으시면 언제든 문의해 주세요.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a 
                href="https://discord.gg/moldclaw" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#5865F2] text-white rounded-lg hover:bg-[#4752C4] transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                Discord 커뮤니티
              </a>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-zinc-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                이메일 문의
              </Link>
              <a 
                href="https://github.com/moldclaw/moldclaw/issues" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-zinc-600 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                버그 리포트
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
